import axios, { AxiosError } from 'axios'
import { limpiarSesion, obtenerSesion } from '@/sesion/almacen'

/**
 * El cliente de la API v1.
 *
 * Bearer desde el almacen de sesion en cada peticion (nunca desde una copia
 * congelada al arrancar) y un 401 cierra la sesion: el token dura 4 horas y
 * cuando vence el panel vuelve al login en vez de mostrar pantallas vacias.
 */
export const cliente = axios.create({
  baseURL: '/api/v1',
  headers: { Accept: 'application/json' },
  // Un booleano de JS se serializa "true"/"false", y la regla `boolean` de
  // Laravel no acepta esas dos palabras: solo true, false, 1, 0, "1" y "0".
  // Sin esto, ?con_coordenada=true responde 422 --medido contra :8010 el
  // 9-sep-- y el filtro de la cartera no funciona. Se traduce aqui, en un solo
  // lugar, en vez de dejar que cada pantalla mande 1 o 0 a mano.
  paramsSerializer: { serialize: serializarParametros },
})

/** Se exporta para poder probarla: con un adaptador falso axios no la llama. */
export function serializarParametros(parametros: Record<string, unknown>): string {
  const salida = new URLSearchParams()

  for (const [clave, valor] of Object.entries(parametros)) {
    if (valor === null || valor === undefined || valor === '') {
      continue
    }

    if (typeof valor === 'boolean') {
      salida.set(clave, valor ? '1' : '0')
      continue
    }

    if (Array.isArray(valor)) {
      for (const uno of valor) {
        salida.append(`${clave}[]`, typeof uno === 'boolean' ? (uno ? '1' : '0') : String(uno))
      }

      continue
    }

    salida.set(clave, String(valor))
  }

  return salida.toString()
}

cliente.interceptors.request.use((config) => {
  const token = obtenerSesion()?.token

  if (token !== undefined) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }

  return config
})

cliente.interceptors.response.use(undefined, (error: unknown) => {
  if (error instanceof AxiosError && error.response?.status === 401 && obtenerSesion() !== null) {
    limpiarSesion('expiro')
  }

  return Promise.reject(error)
})

/** Los errores de validacion de Laravel: {campo: [mensaje, ...]}. */
export type ErroresDeCampo = Record<string, string[]>

export type ErrorApi = {
  estado: number | null
  mensaje: string
  errores: ErroresDeCampo
  esDeRed: boolean
}

const MENSAJE_DE_RED =
  'No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.'

/**
 * Traduce cualquier fallo a algo que se pueda mostrar. Un error de red no es
 * una pantalla rota: es un mensaje y un boton de reintentar.
 */
export function describirError(error: unknown): ErrorApi {
  if (!(error instanceof AxiosError)) {
    return {
      estado: null,
      mensaje: 'Ocurrió un error inesperado.',
      errores: {},
      esDeRed: false,
    }
  }

  const respuesta = error.response

  if (respuesta === undefined) {
    return { estado: null, mensaje: MENSAJE_DE_RED, errores: {}, esDeRed: true }
  }

  const cuerpo = respuesta.data as { message?: string; errors?: ErroresDeCampo } | undefined

  return {
    estado: respuesta.status,
    mensaje: cuerpo?.message ?? mensajePorEstado(respuesta.status),
    errores: cuerpo?.errors ?? {},
    esDeRed: false,
  }
}

function mensajePorEstado(estado: number): string {
  switch (estado) {
    case 401:
      return 'Tu sesión expiró. Entra de nuevo.'
    case 403:
      return 'No tienes permiso para hacer esto.'
    case 404:
      return 'No encontramos lo que buscas.'
    case 429:
      return 'Demasiados intentos. Espera un momento y vuelve a probar.'
    default:
      return estado >= 500
        ? 'El servidor tuvo un problema. Inténtalo de nuevo en unos minutos.'
        : 'No pudimos completar la operación.'
  }
}

/** El primer mensaje de un campo, para pintarlo bajo el input. */
export function errorDeCampo(error: ErrorApi | null, campo: string): string | undefined {
  return error?.errores[campo]?.[0]
}
